import styled from "@emotion/styled";
import type { FC } from "react";
import translate from "src/lib/translate";
import { RefillColors } from "src/constants/refillColors";
import type { RefillCountdownTone } from "src/state/RefillRequirementsCubit/RefillRequirementsTypes";

const SIZE = 32;
// Radius chosen so the ring fills the box: RADIUS + STROKE/2 = SIZE/2.
const STROKE = 3;
const RADIUS = SIZE / 2 - STROKE / 2; // 14.5
const CIRCUMFERENCE = 2 * Math.PI * RADIUS;

// Ring arc color per tone: green on track, orange running out, red once overdue (Figma
// Primary/criticalRed). The day count inside the ring takes the same color.
const RING_COLORS: Record<RefillCountdownTone, string> = {
  green: RefillColors.inRange,
  orange: RefillColors.sunriseOrange,
  red: RefillColors.criticalRed
};

/** Translucent glass pill (Figma: 👑 Countdown). Sits over the hero photo. */
const Pill = styled.div`
  display: inline-flex;
  align-self: flex-start;
  align-items: center;
  gap: 0.375rem;
  padding: 1px 12px 1px 1px;
  border-radius: 120px;
  background: rgba(255, 255, 255, 0.16);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  border: 0.5px solid rgba(255, 255, 255, 0.25);
`;

const RingBox = styled.div`
  position: relative;
  width: ${SIZE}px;
  height: ${SIZE}px;
  flex-shrink: 0;
`;

const Ring = styled.svg`
  transform: rotate(-90deg);
`;

// The number matches the arc rather than the label (Figma: green "3" beside white copy), so
// the ring reads as one unit at a glance.
const Days = styled("span", { shouldForwardProp: (p) => p !== "$color" })<{
  $color: string;
}>`
  position: absolute;
  inset: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.875rem;
  font-weight: 500;
  color: ${({ $color }) => $color};
`;

const Label = styled.span`
  font-size: 0.875rem;
  font-weight: 500;
  color: var(--color-white);
  white-space: nowrap;
`;

// `count` drives i18next pluralization (day/days); the number itself renders in the ring.
const COUNTDOWN_LABEL_KEYS = {
  beforeWindow: "prepareRefillDialog.v2.countdown.daysUntilOpen",
  overdue: "prepareRefillDialog.v2.countdown.daysOverdue",
  remaining: "prepareRefillDialog.v2.countdown.daysLeft"
} as const;

/**
 * Countdown for the hub hero. `fraction` (0–1) fills the ring, `tone` picks green (on
 * track) vs orange (running out). When `overdue`, `days` is the days-past-due count and
 * the label flips to "days overdue" (the ring is empty — pass `fraction={0}`). When
 * `beforeWindow`, `days` counts to the window opening rather than the target date.
 */
const CountdownRing: FC<{
  days: number;
  fraction: number;
  tone?: RefillCountdownTone;
  overdue?: boolean;
  beforeWindow?: boolean;
}> = ({
  days,
  fraction,
  tone = "green",
  overdue = false,
  beforeWindow = false
}) => {
  const clamped = Math.min(1, Math.max(0, fraction));
  const dashArray = `${clamped * CIRCUMFERENCE} ${CIRCUMFERENCE}`;
  const color = RING_COLORS[tone];
  // Pre-window wins over overdue: a renewal cannot be past its target before it has opened,
  // so if both were somehow set the "days until refill opens" reading is the truthful one.
  const labelKey = beforeWindow
    ? COUNTDOWN_LABEL_KEYS.beforeWindow
    : overdue
      ? COUNTDOWN_LABEL_KEYS.overdue
      : COUNTDOWN_LABEL_KEYS.remaining;
  const label = translate(labelKey, { count: days });

  return (
    <Pill>
      <RingBox>
        <Ring width={SIZE} height={SIZE} viewBox={`0 0 ${SIZE} ${SIZE}`}>
          <circle
            cx={SIZE / 2}
            cy={SIZE / 2}
            r={RADIUS}
            fill="none"
            stroke="rgba(255, 255, 255, 0.35)"
            strokeWidth={STROKE}
          />
          {clamped > 0 && (
            <circle
              cx={SIZE / 2}
              cy={SIZE / 2}
              r={RADIUS}
              fill="none"
              stroke={color}
              strokeWidth={STROKE}
              strokeLinecap="round"
              strokeDasharray={dashArray}
            />
          )}
        </Ring>
        <Days $color={color}>{days}</Days>
      </RingBox>
      <Label>{label}</Label>
    </Pill>
  );
};

export default CountdownRing;
